import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { useRegister } from '@presentation/hooks/useAuth';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors } from '@shared/utils/formErrors';
import { paths } from '@app/routes/paths';
import { APP_NAME } from '@shared/constants';

interface RegisterFormValues {
  name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export const RegisterPage = () => {
  const navigate = useNavigate();
  const register = useRegister();
  const [form] = Form.useForm<RegisterFormValues>();

  const handleFinish = (values: RegisterFormValues) => {
    register.mutate(values, {
      onSuccess: () => navigate(paths.dashboard, { replace: true }),
      onError: (error) => {
        if (error instanceof ValidationError) {
          setAntdFormErrors(form, error.errors);
        }
      },
    });
  };

  const generalError =
    register.error && !(register.error instanceof ValidationError) ? register.error.message : null;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <img src="/apilab-icon-64.png" alt="" width={40} height={40} className="mb-2" />
        <Typography.Title level={4} className="!mb-1">
          Create account
        </Typography.Title>
        <Typography.Text type="secondary">Start building with {APP_NAME}.</Typography.Text>

        <Form form={form} layout="vertical" className="mt-6" onFinish={handleFinish}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input prefix={<UserOutlined />} autoComplete="name" size="large" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Email is required' }, { type: 'email' }]}
          >
            <Input prefix={<MailOutlined />} autoComplete="email" size="large" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" size="large" />
          </Form.Item>

          <Form.Item
            name="passwordConfirmation"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" size="large" />
          </Form.Item>

          {generalError && <Alert type="error" showIcon title={generalError} className="mb-4" />}

          <Button type="primary" htmlType="submit" size="large" block loading={register.isPending}>
            Create account
          </Button>

          <div className="mt-4 text-center">
            <Typography.Text type="secondary">Already have an account? </Typography.Text>
            <Link to={paths.login}>Sign in</Link>
          </div>
        </Form>
      </Card>
    </div>
  );
};
